import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Flex } from '@chakra-ui/react';
import { Modal, Button } from '@morressier/alembic';
import _ from 'lodash';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models/submission';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { FormFieldTablesInput } from '@publishing-engine/domain/types/article';
import Tables, {
  ITablesValue
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Tables';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: ITablesValue) => void;
  formField: FormFieldTablesInput;
  value?: IFiguresAndTablesFile;
  submissionContextId: string;
}

const TablesUpdateModal: React.FC<Props> = props => {
  const { isOpen, onClose, onSubmit, formField, submissionContextId } = props;

  const defaultValues = _.set(
    {} as ITablesValue,
    formField.caseDataFields.main,
    props.value
  );

  const schema = generateValidationSchema({
    Tables: formField
  });

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const validateAndSubmit = (data: ITablesValue) => {
    if (!validateTables(data)) {
      scrollToFirstError();
      return;
    }
    onSubmit(data);
    onClose();
  };

  const validateTables = (data: ITablesValue): boolean => {
    let isValid = true;

    // Clear any previous errors on tables
    methods.clearErrors('fileSet.Tables');

    const tables = data.fileSet?.Tables;

    // If no tables array → valid
    if (!tables || tables.length === 0) return true;

    // Check if all tables are optional (completely empty)
    const allEmpty = tables.every((tab: any) => {
      const noCaption = !tab.caption || tab.caption.trim() === '';
      const noFile = !tab.file;
      return noCaption && noFile;
    });

    if (allEmpty) {
      // Entire block optional → valid
      return true;
    }

    // Track duplicates (case-insensitive, trimmed)
    const seenCaptions = new Set<string>();

    // Validate each figure
    tables.forEach((tab: any, idx: number) => {
      const captionRaw = tab.caption ?? '';
      const caption = captionRaw.trim();
      // Duplicate caption check: only for non-empty captions
      if (caption !== '') {
        const normalized = caption.toLowerCase();
        if (seenCaptions.has(normalized)) {
          methods.setError(`fileSet.Tables.${idx}.caption`, {
            type: 'manual',
            message: 'Duplicate caption not allowed for tables.'
          });
          isValid = false;
        } else {
          seenCaptions.add(normalized);
        }
      }
    });

    return isValid;
  };

  const checkIsFileLoading = () => {
    const values = methods.getValues();

    return (
      values.fileSet?.Tables?.some(table => table.file?.isUploading) ?? false
    );
  };

  const isFileUploading = checkIsFileLoading();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="955px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(data => {
            validateAndSubmit(data);
          })}
        >
          <Flex
            gap={4}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
            alignItems="center"
            padding={4}
          >
            <Flex width="100%" alignSelf="center">
              <Tables
                config={formField}
                submissionId={submissionContextId}
                isFromEditModal={true}
              />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                borderRadius="small"
                disabled={isFileUploading}
              >
                Exit Without Saving
              </Button>
              <Button
                variant="primary"
                type="submit"
                borderRadius="small"
                disabled={isFileUploading}
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default TablesUpdateModal;
